import { twMerge } from 'tailwind-merge';
import { ResponsiveImageWithHover } from './responsive-image-with-hover';
import {
  getCellBreakpointValues,
  getCellAssetSources,
  type ResponsiveBooleans,
} from './power-cell-getters';

const POSITION = {
  TOP_LEFT: 'top_left',
  TOP_RIGHT: 'top_right',
  BOTTOM_LEFT: 'bottom_left',
  BOTTOM_RIGHT: 'bottom_right',
  CENTER: 'center',
};

export function getAssetPosition({
  assets,
  assetPositionKey,
  switches,
  classNames,
}: {
  assets: any;
  assetPositionKey: string;
  switches: ResponsiveBooleans;
  classNames?: string;
}) {
  const keys = getCellBreakpointValues({
    config: assets,
    key: assetPositionKey,
    prevBreakpointFallback: true,
  });

  return twMerge(
    // top_left
    switches.mobile &&
      keys.mobile === POSITION.TOP_LEFT &&
      'mobile:max-tablet:top-0 mobile:max-tablet:left-0 mobile:max-tablet:absolute',
    switches.tablet &&
      keys.tablet === POSITION.TOP_LEFT &&
      'tablet:max-desktop:top-0 tablet:max-desktop:left-0 tablet:max-desktop:absolute',
    switches.desktop &&
      keys.desktop === POSITION.TOP_LEFT &&
      'desktop:max-large-desktop:top-0 desktop:max-large-desktop:left-0 desktop:max-large-desktop:absolute',
    switches['large-desktop'] &&
      keys['large-desktop'] === POSITION.TOP_LEFT &&
      'large-desktop:top-0 large-desktop:left-0 large-desktop:absolute',
    // top_left

    //top_right
    switches.mobile &&
      keys.mobile === POSITION.TOP_RIGHT &&
      'mobile:max-tablet:top-0 mobile:max-tablet:right-0 mobile:max-tablet:absolute',
    switches.tablet &&
      keys.tablet === POSITION.TOP_RIGHT &&
      'tablet:max-desktop:top-0 tablet:max-desktop:right-0 tablet:max-desktop:absolute',
    switches.desktop &&
      keys.desktop === POSITION.TOP_RIGHT &&
      'desktop:max-large-desktop:top-0 desktop:max-large-desktop:right-0 desktop:max-large-desktop:absolute',
    switches['large-desktop'] &&
      keys['large-desktop'] === POSITION.TOP_RIGHT &&
      'large-desktop:top-0 large-desktop:right-0 large-desktop:absolute',
    //top_right

    // bottom_left
    switches.mobile &&
      keys.mobile === POSITION.BOTTOM_LEFT &&
      'mobile:max-tablet:bottom-0 mobile:max-tablet:left-0 mobile:max-tablet:absolute',
    switches.tablet &&
      keys.tablet === POSITION.BOTTOM_LEFT &&
      'tablet:max-desktop:bottom-0 tablet:max-desktop:left-0 tablet:max-desktop:absolute',
    switches.desktop &&
      keys.desktop === POSITION.BOTTOM_LEFT &&
      'desktop:max-large-desktop:bottom-0 desktop:max-large-desktop:left-0 desktop:max-large-desktop:absolute',
    switches['large-desktop'] &&
      keys['large-desktop'] === POSITION.BOTTOM_LEFT &&
      'large-desktop:bottom-0 large-desktop:left-0 large-desktop:absolute',
    // bottom_left

    // bottom_right
    switches.mobile &&
      keys.mobile === POSITION.BOTTOM_RIGHT &&
      'mobile:max-tablet:bottom-0 mobile:max-tablet:right-0 mobile:max-tablet:absolute',
    switches.tablet &&
      keys.tablet === POSITION.BOTTOM_RIGHT &&
      'tablet:max-desktop:bottom-0 tablet:max-desktop:right-0 tablet:max-desktop:absolute',
    switches.desktop &&
      keys.desktop === POSITION.BOTTOM_RIGHT &&
      'desktop:max-large-desktop:bottom-0 desktop:max-large-desktop:right-0 desktop:max-large-desktop:absolute',
    switches['large-desktop'] &&
      keys['large-desktop'] === POSITION.BOTTOM_RIGHT &&
      'large-desktop:bottom-0 large-desktop:right-0 large-desktop:absolute',
    // bottom_right

    // center
    switches.mobile &&
      keys.mobile === POSITION.CENTER &&
      'mobile:max-tablet:relative',
    switches.tablet &&
      keys.tablet === POSITION.CENTER &&
      'tablet:max-desktop:relative',
    switches.desktop &&
      keys.desktop === POSITION.CENTER &&
      'desktop:max-large-desktop:relative',
    switches['large-desktop'] &&
      keys['large-desktop'] === POSITION.CENTER &&
      'large-desktop:relative',
    // center

    classNames || ''
  );
}

export function getParentPosition({
  assets,
  assetPositionKey,
  switches,
  classNames,
}: {
  assets: any;
  assetPositionKey: string;
  switches: ResponsiveBooleans;
  classNames?: string;
}) {
  const keys = getCellBreakpointValues({
    config: assets,
    key: assetPositionKey,
    prevBreakpointFallback: true,
  });

  return twMerge(
    // center
    switches.mobile &&
      keys.mobile === POSITION.CENTER &&
      'mobile:max-tablet:flex mobile:max-tablet:items-center mobile:max-tablet:justify-center',
    switches.tablet &&
      keys.tablet === POSITION.CENTER &&
      'tablet:max-desktop:flex tablet:max-desktop:items-center tablet:max-desktop:justify-center',
    switches.desktop &&
      keys.desktop === POSITION.CENTER &&
      'desktop:max-large-desktop:flex desktop:max-large-desktop:items-center desktop:max-large-desktop:justify-center',
    switches['large-desktop'] &&
      keys['large-desktop'] === POSITION.CENTER &&
      'large-desktop:flex large-desktop:items-center large-desktop:justify-center',
    // center

    classNames || ''
  );
}

export function getImageClassNamesOnHover({
  switches,
}: {
  switches: ResponsiveBooleans;
}) {
  return twMerge(
    switches.mobile && 'group-hover:hidden',
    switches.tablet && 'group-hover:hidden',
    switches.desktop && 'group-hover:hidden',
    switches['large-desktop'] && 'group-hover:hidden'
  );
}

const CLASSNAMES =
  'h-content-badge-height mb-content-badge-margin-bottom ml-content-badge-margin-left mr-content-badge-margin-right mt-content-badge-margin-top w-content-badge-width';

export function ContentBadge({ entry, assets }: { entry: any; assets: any }) {
  // image
  const contentBadgeSwitches = getCellBreakpointValues({
    config: assets,
    key: 'content_badge_switch',
  }) as ResponsiveBooleans;

  const imageSources = getCellAssetSources({
    assets,
    assetKey: 'static_badge',
    assetSwitchKey: 'content_badge_switch',
  });

  const imagePosition = getAssetPosition({
    assets,
    assetPositionKey: 'content_badge_position',
    switches: contentBadgeSwitches,
    classNames: CLASSNAMES,
  });

  const imageParentClassNames = getParentPosition({
    assets,
    assetPositionKey: 'content_badge_position',
    switches: contentBadgeSwitches,
    classNames: 'absolute z-[5] aspect-cell-image w-full',
  });
  // image

  // hover
  const contentBadgeHoverSwitches = getCellBreakpointValues({
    config: assets,
    key: 'content_badge_hover_switch',
  }) as ResponsiveBooleans;

  const imageClassNamesOnHover = getImageClassNamesOnHover({
    switches: contentBadgeHoverSwitches,
  });

  const hoverSources = getCellAssetSources({
    assets,
    assetKey: 'static_badge_hover',
    assetSwitchKey: 'content_badge_hover_switch',
  });

  const hoverPosition = getAssetPosition({
    assets,
    assetPositionKey: 'content_badge_position',
    switches: contentBadgeHoverSwitches,
    classNames: CLASSNAMES,
  });

  const hoverParentClassNames = getParentPosition({
    assets,
    assetPositionKey: 'content_badge_position',
    switches: contentBadgeHoverSwitches,
    classNames: 'absolute z-[5] aspect-cell-image w-full',
  });
  // hover

  return (
    <ResponsiveImageWithHover
      imageSources={imageSources}
      hoverSources={hoverSources}
      altText={entry?.title && `${entry?.title} badge`}
      imageClassNames={imagePosition}
      imageClassNamesOnHover={imageClassNamesOnHover}
      hoverClassNames={hoverPosition}
      imageParentClassNames={imageParentClassNames}
      hoverParentClassNames={hoverParentClassNames}
    />
  );
}
